import React, { useCallback, useEffect, useState } from "react";
import {
  deleteSlot,
  listSlots,
  load as loadSlot,
  save as saveSlot,
  AUTOSAVE_SLOT_ID,
  QUICKSAVE_SLOT_ID,
  type SaveSlotSummary,
  type SaveSlotInput,
} from "../../engine/persistence/PersistenceManager.js";
import { getSaveBlockReason } from "../../engine/persistence/saveBlocker.js";
import type { LoadSaveFileResult } from "../../engine/persistence/SaveFile.js";

/**
 * Save/load screen (P07).
 *
 * Mounted either inside the main menu or the Escape overlay. Lists all
 * slots with metadata, plus a "New slot…" entry that prompts for a name.
 * Save / load / delete actions delegate to PersistenceManager. The in-dungeon
 * save-blocker (P08) disables the save buttons when saving is refused.
 */

interface Props {
  readonly onBack: () => void;
  readonly inputProvider: () => SaveSlotInput;
  readonly onLoadComplete?: (result: LoadSaveFileResult) => void;
}

export function SaveLoadScreen({ onBack, inputProvider, onLoadComplete }: Props) {
  const [slots, setSlots] = useState<readonly SaveSlotSummary[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await listSlots();
      setSlots(list);
    } catch (err) {
      setStatus(`Failed to list saves: ${describeError(err)}`);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const blockReason = getSaveBlockReason();

  const handleSave = async (slotId: string, displayName?: string) => {
    if (blockReason !== null) {
      setStatus(`Can't save — ${blockReason}.`);
      return;
    }
    setBusy(true);
    try {
      const input: SaveSlotInput = displayName
        ? { ...inputProvider(), displayName }
        : inputProvider();
      await saveSlot(slotId, input);
      setStatus(`Saved to "${slotId}".`);
      await refresh();
    } catch (err) {
      setStatus(`Save failed: ${describeError(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleLoad = async (slotId: string) => {
    setBusy(true);
    try {
      const result = await loadSlot(slotId);
      setStatus(`Loaded "${slotId}".`);
      onLoadComplete?.(result);
    } catch (err) {
      setStatus(`Load failed: ${describeError(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (slotId: string) => {
    setBusy(true);
    try {
      await deleteSlot(slotId);
      setStatus(`Deleted "${slotId}".`);
      await refresh();
    } catch (err) {
      setStatus(`Delete failed: ${describeError(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleNewSlot = async () => {
    const name = window.prompt("Save slot name", "My Save");
    if (!name) return;
    const slotId = `slot-${Date.now().toString(36)}`;
    await handleSave(slotId, name);
  };

  return (
    <div
      data-testid="save-load-screen"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 360,
        color: "white",
        fontFamily: "monospace",
      }}
    >
      <h2 style={{ margin: 0, fontSize: 18, letterSpacing: 2 }}>SAVE / LOAD</h2>
      {blockReason && (
        <div style={{ color: "#ff9", fontSize: 11 }}>
          Saves disabled — {blockReason}.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflow: "auto" }}>
        {slots.length === 0 ? (
          <p style={{ color: "#888", fontSize: 12, margin: 0 }}>No saves yet.</p>
        ) : (
          slots.map((s) => {
            const canDelete =
              s.slotId !== AUTOSAVE_SLOT_ID && s.slotId !== QUICKSAVE_SLOT_ID;
            return (
              <SlotRow
                key={s.slotId}
                summary={s}
                busy={busy}
                canSave={blockReason === null}
                onSave={() => handleSave(s.slotId)}
                onLoad={() => handleLoad(s.slotId)}
                {...(canDelete
                  ? { onDelete: () => handleDelete(s.slotId) }
                  : {})}
              />
            );
          })
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={handleNewSlot}
          disabled={busy || blockReason !== null}
          style={btnStyle(busy || blockReason !== null)}
        >
          New slot…
        </button>
        <button type="button" onClick={onBack} style={btnStyle(false)}>
          Back
        </button>
      </div>
      {status && (
        <div data-testid="save-load-status" style={{ fontSize: 11, color: "#9f9" }}>
          {status}
        </div>
      )}
    </div>
  );
}

function SlotRow({
  summary,
  busy,
  canSave,
  onSave,
  onLoad,
  onDelete,
}: {
  summary: SaveSlotSummary;
  busy: boolean;
  canSave: boolean;
  onSave: () => void;
  onLoad: () => void;
  onDelete?: () => void;
}) {
  return (
    <div
      data-testid={`slot-row-${summary.slotId}`}
      style={{
        background: "#222",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 4,
        padding: "8px 10px",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>{summary.displayName}</div>
        <div style={{ fontSize: 10, color: "#888" }}>{summary.slotId}</div>
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={busy || !canSave}
        style={btnStyle(busy || !canSave)}
      >
        Save
      </button>
      <button type="button" onClick={onLoad} disabled={busy} style={btnStyle(busy)}>
        Load
      </button>
      {onDelete && (
        <button type="button" onClick={onDelete} disabled={busy} style={btnStyle(busy)}>
          Delete
        </button>
      )}
    </div>
  );
}

function btnStyle(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? "#222" : "#333",
    color: disabled ? "#555" : "white",
    border: "1px solid",
    borderColor: disabled ? "#333" : "rgba(255,255,255,0.3)",
    borderRadius: 4,
    padding: "4px 10px",
    fontFamily: "monospace",
    fontSize: 11,
    cursor: disabled ? "default" : "pointer",
  };
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
